import { Virtualizer } from "@tanstack/react-virtual";
import * as React from "react";

import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services";

import { Timer } from "../../timer";

import { ScrollDirection } from "./LogCache";
import { LogManager } from "./LogManager";

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function* interval(ms: number, signal: AbortSignal) {
  while (!signal.aborted) {
    // Skip polling if tab is not visible
    if (document.visibilityState === "hidden") {
      await new Promise((resolve) =>
        document.addEventListener("visibilitychange", resolve, {
          once: true,
          signal,
        }),
      );
      continue;
    }

    const now = performance.now();
    yield now;
    // best effort attempt at running within deadline
    // takes into account duration of whatever happened during yield
    await delay(ms - (performance.now() - now));
  }
}

type Options = {
  /**
   * Whether to poll for logs. Defaults to `true`.
   */
  enabled?: boolean;
};

export function useLogs(
  logManager: LogManager | null,
  virtualizer: Virtualizer<HTMLDivElement, Element>,
  timer: Timer,
  currentPage: number,
  logsPerPage: number,
  options?: Options,
) {
  const { enabled = true } = options ?? {};

  const [_, setLastCacheUpdate] = React.useState(performance.now());

  const prevVirtualizerState = React.useRef<{
    page: number;
    virtualStart: number;
    virtualEnd: number;
  }>({ page: 0, virtualStart: 0, virtualEnd: 0 });
  const loadLogsIfNeeded = React.useCallback(
    function loadLogsIfNeeded(abortSignal?: AbortSignal) {
      if (logManager === null) {
        return Promise.resolve(false);
      }

      const scrollDirection =
        virtualizer.scrollDirection === "backward"
          ? ScrollDirection.Backward
          : ScrollDirection.Forward;

      const { startIndex: virtualStart = 0, endIndex: virtualEnd = 0 } =
        virtualizer.range ?? {};

      // GM (2025-06-09)
      // The virtualizer's internal state lags React component state.
      // This becomes problematic when seeking or loading the panel at a timestamp:
      // an updated `currentPage` is applied to stale virtual indices,
      // causing a load for a range of non-relevant logs.
      // This is an issue on first-load at a timestamp because the component re-renders multiple times during initialization.
      // In many circumstances, the extraneous load is negligible, but it becomes particularly noticeable
      // on first-load of the panel for Parquet topic data,
      // when the underlying data must be fetched and the ParquetTopicReader "foregrounds" the load of requested logs
      // and backgrounds the remainder.
      // I attempted forcing a recalculation of the virtualizer's internal state on relevant component updates,
      // but was unsuccessful.
      const {
        page: prevPage,
        virtualStart: prevVirtualStart,
        virtualEnd: prevVirtualEnd,
      } = prevVirtualizerState.current;
      prevVirtualizerState.current = {
        page: currentPage,
        virtualStart,
        virtualEnd,
      };
      if (
        prevPage !== currentPage &&
        prevVirtualStart === virtualStart &&
        prevVirtualEnd === virtualEnd
      ) {
        return delay(0);
      }

      // Adjust indices for pagination applied ahead of virtualization
      const start = currentPage * logsPerPage + virtualStart;
      const end = currentPage * logsPerPage + virtualEnd;

      return logManager.loadLogs(
        { start, end },
        scrollDirection,
        timer.playbackRate,
        abortSignal,
      );
    },
    [logManager, virtualizer, timer, currentPage, logsPerPage],
  );

  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    const abortController = new AbortController();

    void (async function pollForLogs() {
      for await (const ts of interval(50, abortController.signal)) {
        if (abortController.signal.aborted) {
          break;
        }
        try {
          const didRun = await loadLogsIfNeeded(abortController.signal);
          if (didRun && !abortController.signal.aborted) {
            // set state to enqueue a component re-render
            setLastCacheUpdate(ts);
          }
        } catch (err) {
          if (isAbortError(err)) {
            return;
          }
          ErrorMonitoringService.captureError(err);
        }
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [enabled, loadLogsIfNeeded]);
}
